import { mount } from 'svelte';
import App from './App.svelte';
import './app.css';
import { initAnalytics } from './lib/analytics';
import { configureUi } from '@ui';
import { navigate } from './lib/router.svelte';
import { optimisticSkoolId } from './lib/community.svelte';
import { roleOf } from './lib/roles.svelte';
import { apiFetch, apiGet, apiSend } from './lib/transport';
import { handle401 } from './lib/admin';
import { ensureHelper, helper, postingAs } from './lib/helper.svelte';
import { initPrivacy } from './lib/privacy.svelte';

// Restore the demo redaction BEFORE mount, so a reload with the mode on cannot paint
// one unredacted frame of a screen full of real names. See lib/privacy.svelte.ts.
initPrivacy();

// PostHog — no-ops unless the prod build's VITE_POSTHOG_KEY is set (prod only).
initAnalytics();

/**
 * Teach the shared UI package what this app can do.
 *
 * Everything the shared components cannot know on their own — which route may be
 * called, how a link is followed, where the community comes from. The Helper
 * installs the same five capabilities against its own, narrower surface.
 */
configureUi({
  apiGet,
  apiFetch,
  apiSend,
  memberCardScope: () => {
    const id = optimisticSkoolId();
    return id == null ? null : String(id);
  },
  memberProfileHref: (memberId) => `/admin/community/members/${memberId}`,
  openMemberProfile: (memberId) => navigate(`/admin/community/members/${memberId}`),
  // The browser already does the right thing with an <a href> in a full page.
  openLink: () => false,
  roleOf,
  /**
   * Who a queued reply publishes as, from the Helper store.
   *
   * `ensureHelper` is idempotent and starts the probe on first call, so asking here
   * keeps the old behaviour exactly: the extension is only probed once something
   * that can queue a reply is actually on screen.
   */
  postsAs: () => {
    void ensureHelper();
    const author = postingAs();
    const name = author?.name || (author?.handle ? '@' + author.handle : null);
    const live = helper.bridge?.liveSkool ?? null;
    const liveWho = live?.name || (live?.handle ? '@' + live.handle : 'another account');
    return {
      name,
      pictureUrl: author?.picture_profile ?? null,
      // Only ever warn on something the bridge has POSITIVELY confirmed — a wrong or
      // signed-out Skool session, or an unpaired install. A silent bridge is "don't
      // know", not a problem: the reply still queues and the Outbox shows the real,
      // server-backed delivery state. Never fabricate a warning from an absent signal.
      warning:
        helper.state === 'account-mismatch'
          ? `This browser is signed in to Skool as ${liveWho} — switch back to ${name ?? 'the paired account'} or it won't send.`
          : helper.state === 'skool-unreadable'
            ? "Can't check your Skool session in this browser — this will wait rather than fail."
            : helper.state === 'signed-out-of-skool'
              ? "Signed out of skool.com in this browser — it won't send until you sign back in."
              : helper.state === 'installed-not-paired'
                ? 'Your Helper is installed here but not signed in — this will wait until it is.'
                : null,
    };
  },
  // A 401 or 402 NAVIGATES — to the login page or to billing — so the composer must
  // show nothing and stop. Null says the app took it from here.
  apiErrorMessage: (err, fallback) =>
    handle401(err, navigate) ? null : err instanceof Error ? err.message : fallback,
});

const target = document.getElementById('app');
if (!target) {
  throw new Error('#app root element missing from index.html');
}

const app = mount(App, { target });

export default app;
